import React, { useState, useEffect, useCallback } from "react";
import { MessageSquare, Settings as SettingsIcon, BellOff, RotateCcw, RefreshCw, Loader2, Trash2, Save, Check } from "lucide-react";

const DEFAULT_PENDING_TEMPLATE =
  "Hi {name}, this is a reminder that rent of {amount} for house {house} is due on {dueDate}. Please arrange payment on time. Thank you.";
const DEFAULT_OVERDUE_TEMPLATE =
  "Hi {name}, your rent for house {house} is now OVERDUE. An amount of {balance} is outstanding. Kindly settle immediately. Thank you.";
import { useData } from "../context/DataContext";
import { formatDateTime } from "../utils/format";
import { useSearch, matchesQuery } from "../context/SearchContext";
import ConfirmDialog from "../components/ConfirmDialog";
import TimePicker from "../components/TimePicker";
import SmsExportButton from "../components/SmsExportButton";
import PropertyAutomationPicker from "../components/PropertyAutomationPicker";

export default function SmsReminders() {
  const { communications, refreshCommunications, deleteCommunication, properties, automationSettings, updateAutomationSettings } = useData();
  const { query } = useSearch();
  const [showSettings, setShowSettings] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    if (showSettings) setDraft({ ...automationSettings });
  }, [showSettings, automationSettings]);

  const updateDraft = useCallback((patch) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
    setJustSaved(false);
  }, []);

  const isDirty = draft && JSON.stringify(draft) !== JSON.stringify(automationSettings);

  const handleSave = async () => {
    if (!draft || !isDirty) return;
    setSaving(true);
    try {
      await updateAutomationSettings(draft);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
    } catch {}
    setSaving(false);
  };

  const d = draft || automationSettings;

  const sms = communications
    .filter((c) => c.type === "sms")
    .filter((c) => matchesQuery(query, c.tenantName, c.houseNumber, c.message))
    .sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta;
    });

  const s = automationSettings;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">SMS Reminders</h2>
            <span className="rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300">
              {sms.length} {sms.length === 1 ? "message" : "messages"}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            History of SMS reminders sent to tenants.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SmsExportButton />
          <button
            className="btn-secondary text-sm"
            disabled={refreshing}
            onClick={async () => {
              setRefreshing(true);
              try { await refreshCommunications(); } catch {}
              setRefreshing(false);
            }}
            aria-label="Refresh"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="hidden sm:inline">{refreshing ? "Refreshing…" : "Refresh"}</span>
          </button>
          <button
            onClick={() => setShowSettings((v) => !v)}
            className={`btn-secondary text-sm ${showSettings ? "bg-gray-100" : ""}`}
            aria-label="Automation settings"
          >
            <SettingsIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Settings</span>
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="card p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Automatic SMS reminders</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                When enabled, the system will automatically send SMS reminders to tenants based on the schedule below.
              </p>
            </div>
            <button
              onClick={() => updateDraft({ autoSmsEnabled: !d.autoSmsEnabled })}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                d.autoSmsEnabled ? "bg-indigo-600" : "bg-gray-200"
              }`}
              role="switch"
              aria-checked={d.autoSmsEnabled}
              aria-label="Toggle automatic SMS"
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                  d.autoSmsEnabled ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>

          {d.autoSmsEnabled && (
            <div className="mt-6 grid gap-5 border-t border-gray-100 dark:border-gray-700 pt-5 sm:grid-cols-2">
              <div>
                <label className="label-base">Start sending</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="14"
                    value={d.smsDaysBeforeDue}
                    onChange={(e) =>
                      updateDraft({
                        smsDaysBeforeDue: Math.max(1, Math.min(14, Number(e.target.value) || 1)),
                      })
                    }
                    className="input-base w-20 text-center"
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">days before due date</span>
                </div>
              </div>
              <div>
                <label className="label-base">Send at</label>
                <div className="flex items-center gap-2">
                  <TimePicker
                    value={d.smsTimeOfDay || "08:00"}
                    onChange={(v) => updateDraft({ smsTimeOfDay: v })}
                  />
                  <span className="text-sm text-gray-600 dark:text-gray-400">EAT</span>
                </div>
              </div>
              <div>
                <label className="label-base">Frequency</label>
                <div className="flex gap-2">
                  {[
                    { v: "daily", l: "Every day" },
                    { v: "once", l: "Once only" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      onClick={() => updateDraft({ smsFrequency: o.v })}
                      className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                        d.smsFrequency === o.v
                          ? "border-indigo-600 bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300"
                          : "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                      }`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label-base">Target tenants</label>
                <label className="mt-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={d.smsIncludePending}
                    onChange={(e) =>
                      updateDraft({ smsIncludePending: e.target.checked })
                    }
                    className="checkbox-base"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Pending tenants (upcoming due)</span>
                </label>
                <label className="mt-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={d.smsIncludeOverdue}
                    onChange={(e) =>
                      updateDraft({ smsIncludeOverdue: e.target.checked })
                    }
                    className="checkbox-base"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Overdue tenants</span>
                </label>
              </div>
              <PropertyAutomationPicker
                properties={properties}
                includedIds={d.includedSmsProperties}
                onChange={(ids) => updateDraft({ includedSmsProperties: ids })}
                label="Properties to include"
                helperText="Only checked properties will receive automatic SMS reminders. New properties start unchecked."
              />
              {d.smsIncludePending && (
                <div className="sm:col-span-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <label className="label-base">Pending reminder template</label>
                    <button
                      type="button"
                      onClick={() =>
                        updateDraft({ smsPendingTemplate: DEFAULT_PENDING_TEMPLATE })
                      }
                      className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
                    >
                      <RotateCcw className="h-3 w-3" />
                      Reset to default
                    </button>
                  </div>
                  <textarea
                    className="input-base min-h-[80px] resize-y font-mono text-xs"
                    value={d.smsPendingTemplate}
                    onChange={(e) =>
                      updateDraft({ smsPendingTemplate: e.target.value })
                    }
                  />
                </div>
              )}
              {d.smsIncludeOverdue && (
                <div className="sm:col-span-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <label className="label-base">Overdue reminder template</label>
                    <button
                      type="button"
                      onClick={() =>
                        updateDraft({ smsOverdueTemplate: DEFAULT_OVERDUE_TEMPLATE })
                      }
                      className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
                    >
                      <RotateCcw className="h-3 w-3" />
                      Reset to default
                    </button>
                  </div>
                  <textarea
                    className="input-base min-h-[80px] resize-y font-mono text-xs"
                    value={d.smsOverdueTemplate}
                    onChange={(e) =>
                      updateDraft({ smsOverdueTemplate: e.target.value })
                    }
                  />
                </div>
              )}
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900 p-4 text-xs text-gray-600 dark:text-gray-400 sm:col-span-2">
                <strong className="text-gray-900 dark:text-white">Placeholders:</strong>{" "}
                <code>{"{name}"}</code>, <code>{"{house}"}</code>,{" "}
                <code>{"{amount}"}</code>, <code>{"{balance}"}</code>,{" "}
                <code>{"{dueDate}"}</code>
                <br />
                <strong className="text-gray-900 dark:text-white">How it works:</strong> The system sends
                an SMS to each qualifying tenant starting {d.smsDaysBeforeDue} day
                {d.smsDaysBeforeDue === 1 ? "" : "s"} before their due date
                {d.smsFrequency === "daily"
                  ? ", repeating daily until payment is received."
                  : "."}
                {d.smsIncludeOverdue && " Overdue tenants also receive daily reminders until paid."}
              </div>
            </div>
          )}

          {!d.autoSmsEnabled && (
            <div className="mt-4 flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
              <BellOff className="h-4 w-4 text-gray-400" />
              Automatic SMS reminders are turned off. You can still send manual reminders from the
              Tenants page.
            </div>
          )}

          <div className="mt-5 flex items-center justify-end gap-3 border-t border-gray-100 dark:border-gray-700 pt-4">
            {justSaved && (
              <span className="flex items-center gap-1 text-sm font-medium text-green-600">
                <Check className="h-4 w-4" /> Saved
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={!isDirty || saving}
              className={`btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold ${
                !isDirty && !justSaved ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? "Saving…" : isDirty ? "Save changes" : "Save changes"}
            </button>
          </div>
        </div>
      )}

      {sms.length === 0 ? (
        <div className="card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600">
            <MessageSquare className="h-6 w-6" />
          </div>
          <h3 className="mt-4 text-base font-semibold text-gray-900 dark:text-white">No SMS sent yet</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {s.autoSmsEnabled
              ? "Automatic reminders are enabled. SMS history will appear here."
              : "Send an SMS reminder from the Tenants page to see records here."}
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {sms.map((c) => {
            const property = properties.find((p) => p.id === c.propertyId);
            return (
              <li key={c.id} className="card overflow-hidden">
                <div className="flex items-start justify-between gap-3 px-5 py-4">
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                      <MessageSquare className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {c.tenantName}
                        </span>
                        {c.phone && <span className="text-xs text-gray-400 dark:text-gray-500">{c.phone}</span>}
                        <SmsStatusBadge status={c.status} />
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {property?.name || "—"} · House {c.houseNumber} ·{" "}
                        {formatDateTime(c.createdAt)}
                      </div>
                      <div className="mt-2 rounded-lg bg-gray-50 dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-300">
                        {c.message}
                      </div>
                    </div>
                  </div>
                  <button
                    className="self-start rounded-md p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-red-600 cursor-pointer"
                    onClick={() => setConfirmDelete(c)}
                    aria-label="Delete SMS"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Delete SMS record?"
        description="This will remove the SMS record and allow the scheduler to re-send if the tenant still qualifies."
        confirmLabel="Delete"
        tone="danger"
        loading={deleting}
        onConfirm={async () => {
          setDeleting(true);
          try {
            await deleteCommunication(confirmDelete.id);
          } catch (err) {
            console.error("Delete failed:", err);
          }
          setDeleting(false);
          setConfirmDelete(null);
        }}
      />
    </div>
  );
}

function SmsStatusBadge({ status }) {
  const styles = {
    sent: "bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 ring-green-600/20 dark:ring-green-500/30",
    failed: "bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-400 ring-red-600/20 dark:ring-red-500/30",
  };
  const style = styles[status] || styles.sent;
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${style}`}>
      {status || "sent"}
    </span>
  );
}
